import { useState } from 'react';
import { Banknote, X } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useLive } from '../lib/useLive';

type Slip = {
  period_id: string; period_key: string; gross_cents: number;
  starts_on: string; ends_on: string; seen: boolean;
};

/**
 * BARRA DE "TU RECIBO ESTÁ LISTO".
 *
 * Azul, como la del Employee of the Month: es una buena noticia, no una alarma. Al hacer click abre
 * el PDF directo — la gracia es que nadie tenga que ir a buscarlo a ningún lado.
 *
 * Se cierra sola cuando el recibo se abre (el backend marca `opened_at` con la misma llamada), así
 * que no hay un "visto" aparte que mantener sincronizado. Y muestra el MONTO en la barra: el que
 * cobra siempre quiere saber cuánto antes de abrir nada.
 */
export function PayslipBanner() {
  const { t, lang } = useI18n();
  // Igual que el anuncio: consultaba una sola vez al montar, así que el recibo aparecía recién
  // cuando alguien recargaba. Cobrar es justo lo que uno quiere saber sin tener que apretar F5.
  const { data } = useLive(() => api.get<{ slips: Slip[] }>('/api/timeclock/my-slips'), { everyMs: 120_000 });
  const [cerrados, setCerrados] = useState<string[]>([]);
  const slip = (data?.slips ?? []).find((s) => !s.seen && !cerrados.includes(s.period_id)) ?? null;
  if (!slip) return null;

  const cerrar = () => {
    const id = slip.period_id;
    setCerrados((p) => [...p, id]);
    api.post(`/api/timeclock/my-slips/${id}/seen`, {}).catch(() => {});
  };
  const abrir = () => {
    // 'me' en vez del id: la pantalla del empleado no tiene por qué conocer su propio id.
    window.open(`/api/timeclock/payroll/${slip.period_id}/slip/me/pdf?lang=${lang}`, '_blank', 'noopener');
    cerrar();
  };

  const fmt = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString(lang === 'es' ? 'es' : 'en', { month: 'short', day: 'numeric' });

  return (
    <div className="eom-banner" role="status">
      <button className="eom-banner-main" onClick={abrir}>
        <span className="eom-banner-tag"><Banknote size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('slip.tag')}</span>
        <span className="eom-banner-msg">
          {t('slip.msg', { from: fmt(slip.starts_on), to: fmt(slip.ends_on) })}
          {' · '}
          <b>${(slip.gross_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b>
        </span>
        <span className="eom-banner-go">{t('slip.open')} →</span>
      </button>
      <button className="eom-banner-x" onClick={cerrar} title={t('ia.dismiss')}><X size={14} strokeWidth={1.75} /></button>
    </div>
  );
}
